import React from 'react';
import Header from '@/components/Header';
import MinimalFooter from '@/components/MinimalFooter';
import { 
  Headphones, 
  Clock, 
  Volume2, 
  VolumeX, 
  Star, 
  Waves,
  ListTodo,
  BarChart3,
  PhoneOff,
  Sliders,
  Brain
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

const Guide = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <div className="container mx-auto px-4 py-6 flex-grow">
        <h1 className="text-3xl font-bold text-center mt-6 mb-2">How to Use BinauralFlow</h1>
        
        <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-10">
          Learn how to get the most out of your binaural beats experience with our comprehensive 
          usage guide
        </p>
        
        <Tabs defaultValue="getting-started" className="w-full">
          <div className="flex justify-center mb-8">
            <TabsList className="w-full max-w-lg mx-auto">
              <TabsTrigger value="getting-started" className="flex-1">Getting Started</TabsTrigger>
              <TabsTrigger value="frequencies" className="flex-1">Understanding Frequencies</TabsTrigger>
              <TabsTrigger value="advanced" className="flex-1">Advanced Features</TabsTrigger>
            </TabsList>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="md:col-span-1 h-full">
              <Card className="h-full sticky top-24">
                <CardHeader>
                  <CardTitle className="text-lg">Quick Tips</CardTitle>
                  <CardDescription>Essential information for beginners</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Headphones className="h-5 w-5 text-primary mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Use Headphones</p>
                      <p className="text-xs text-muted-foreground">For proper binaural effect, headphones are essential</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Start with 15-20 minutes</p>
                      <p className="text-xs text-muted-foreground">Beginners should limit sessions to 15-20 minutes</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Volume2 className="h-5 w-5 text-primary mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Moderate Volume</p>
                      <p className="text-xs text-muted-foreground">Keep volume at a comfortable level, not too loud</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="md:col-span-3">
              <TabsContent value="getting-started" className="space-y-6 mt-0">
                <div className="space-y-4">
                  <h2 className="text-2xl font-medium">Getting Started with BinauralFlow</h2>
                  <p className="text-muted-foreground mb-8">
                    BinauralFlow is designed to help you harness the power of binaural beats for focus, relaxation, meditation, and sleep. Here's how to get started with your first session.
                  </p>
                  
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/80 text-primary-foreground h-6 w-6 rounded-full flex items-center justify-center text-sm font-medium">1</div>
                        <h3 className="text-xl font-medium">Choose a Preset</h3>
                      </div>
                      <p className="pl-8 text-muted-foreground">
                        Select one of our carefully designed presets based on your current needs - Focus, Meditation, Relaxation, or Sleep.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/80 text-primary-foreground h-6 w-6 rounded-full flex items-center justify-center text-sm font-medium">2</div>
                        <h3 className="text-xl font-medium">Put on Headphones</h3>
                      </div>
                      <p className="pl-8 text-muted-foreground">
                        For binaural beats to work properly, you must use stereo headphones or earbuds. The effect is created when each ear receives a slightly different frequency.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/80 text-primary-foreground h-6 w-6 rounded-full flex items-center justify-center text-sm font-medium">3</div>
                        <h3 className="text-xl font-medium">Adjust Volume</h3>
                      </div>
                      <p className="pl-8 text-muted-foreground">
                        Set the volume to a comfortable level - not too loud, but audible enough to perceive the beats. You can also mix in ambient sounds if desired.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/80 text-primary-foreground h-6 w-6 rounded-full flex items-center justify-center text-sm font-medium">4</div>
                        <h3 className="text-xl font-medium">Start Your Session</h3>
                      </div>
                      <p className="pl-8 text-muted-foreground">
                        Press play and find a comfortable position. Close your eyes and allow the beats to guide your brainwave activity. For beginners, start with 15-20 minute sessions.
                      </p>
                    </div>
                  </div>
                </div>
                
                <Separator className="my-8" />
                
                <div className="space-y-4">
                  <h2 className="text-2xl font-medium">Binaural Beats Best Practices</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <PhoneOff className="h-5 w-5 text-muted-foreground" />
                          Minimize Distractions
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Find a quiet place where you won't be disturbed. Put your phone on silent mode and let others know you need uninterrupted time.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Star className="h-5 w-5 text-muted-foreground" />
                          Consistency is Key
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          For best results, use binaural beats regularly. Many users report that daily sessions yield the most noticeable benefits.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Waves className="h-5 w-5 text-muted-foreground" />
                          Experiment with Frequencies
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Different frequencies affect people differently. Don't be afraid to experiment with various presets and custom frequencies to find what works best for you.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <ListTodo className="h-5 w-5 text-muted-foreground" />
                          Track Your Progress
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Keep notes on which frequencies and durations work best for different purposes. Save your favorite sessions for easy access later.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="frequencies" className="space-y-6 mt-0">
                <div className="space-y-4">
                  <h2 className="text-2xl font-medium">Understanding Frequencies</h2>
                  <p className="text-muted-foreground mb-8">
                    Different brainwave frequencies are associated with different mental states. Understanding these can help you choose the right frequency for your goals.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Delta (0.5-4 Hz)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Deep sleep, healing, and restoration. Delta waves dominate during dreamless sleep and are essential for physical recovery. They can help with insomnia and promote deep healing.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Theta (4-8 Hz)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Meditation, creativity, and REM sleep. Theta waves are present during deep meditation and light sleep. They can enhance intuition, creativity, and access to memories and emotions.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Alpha (8-13 Hz)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Relaxation, calmness, and positive thinking. Alpha waves bridge the gap between conscious and subconscious mind. They help reduce stress, enhance learning, and create a state of wakeful relaxation.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Beta (13-30 Hz)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Focus, alertness, and problem-solving. Beta waves are associated with active thinking and concentration. They can improve cognitive performance, attention, and analytical thinking.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Gamma (30-100 Hz)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Peak concentration, high-level processing, and heightened perception. Gamma waves represent the brain at peak performance, associated with advanced cognitive functioning and heightened awareness.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="advanced" className="space-y-6 mt-0">
                <div className="space-y-4">
                  <h2 className="text-2xl font-medium">Advanced Features</h2>
                  <p className="text-muted-foreground mb-8">
                    Once you're comfortable with the basics, explore BinauralFlow's advanced features to customize your experience.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Ambient Sound Mixer</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Enhance your experience by adding background ambient sounds like rain, ocean waves, forest sounds, and white noise. Each sound can be individually adjusted for volume.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Custom Sessions</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Create your own binaural beat sessions by selecting a specific frequency and duration tailored to your needs. Fine-tune the carrier frequency and beat frequency for precise control.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Progressive Sessions</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Experience sessions that gradually transition between frequencies, guiding your brain from one state to another over time. Perfect for meditation that shifts from alertness to deep relaxation.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Favorites</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Save your favorite presets or custom sessions for quick access in future sessions. Build a personal library of sessions that work best for your unique needs.
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Session History</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Keep track of your previous sessions to monitor your usage patterns and preferences. Review your binaural beat journey and identify which sessions were most effective.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
            </div>
          </div>
        </Tabs>
      </div>
      
      <MinimalFooter />
    </div>
  );
};

export default Guide;
